import React, { useState, useRef, useEffect } from 'react';
import { X, CheckCircle, XCircle, Camera, Calendar, MapPin, User, Mail, Euro, Clock } from 'lucide-react';
import jsQR from 'jsqr';

const ScanPage = () => {
  const [manualId, setManualId] = useState('');
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState('');
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const scanIntervalRef = useRef(null);

  // Détecter les caméras disponibles
  useEffect(() => {
    navigator.mediaDevices.enumerateDevices()
      .then(devices => {
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        console.log('📹 Caméras trouvées:', videoDevices);
        setCameras(videoDevices);
        
        // Privilégier la caméra arrière sur mobile
        const backCamera = videoDevices.find(d => d.label.toLowerCase().includes('back') || d.label.toLowerCase().includes('arrière'));
        if (backCamera) {
          setSelectedCamera(backCamera.deviceId);
        } else if (videoDevices.length > 0) {
          setSelectedCamera(videoDevices[0].deviceId);
        }
      })
      .catch(err => {
        console.error('Erreur liste caméras:', err);
      });
  }, []);

  const startScanning = async () => {
    try {
      setError('');
      setResult(null);
      console.log('🎥 Démarrage scan caméra:', selectedCamera);
      
      const constraints = {
        video: {
          deviceId: selectedCamera ? { exact: selectedCamera } : undefined,
          facingMode: selectedCamera ? undefined : { ideal: 'environment' }, // Caméra arrière sur mobile
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('✅ Stream caméra obtenu');
      streamRef.current = stream;
      setScanning(true);
      
      setTimeout(() => {
        if (videoRef.current && streamRef.current) {
          videoRef.current.srcObject = streamRef.current;
          videoRef.current.play().then(() => {
            console.log('✅ Vidéo démarrée');
            
            // Scanner QR code
            scanIntervalRef.current = setInterval(() => {
              if (videoRef.current && canvasRef.current && videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA) {
                try {
                  const video = videoRef.current;
                  const canvas = canvasRef.current;
                  const context = canvas.getContext('2d');
                  
                  canvas.width = video.videoWidth;
                  canvas.height = video.videoHeight;
                  
                  if (canvas.width > 0 && canvas.height > 0) {
                    context.drawImage(video, 0, 0, canvas.width, canvas.height);
                    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
                    
                    const code = jsQR(imageData.data, imageData.width, imageData.height, {
                      inversionAttempts: 'dontInvert',
                    });
                    
                    if (code && code.data) {
                      console.log('✅ QR Code détecté:', code.data);
                      stopScanning();
                      verifyReservation(code.data);
                    }
                  }
                } catch (err) {
                  console.error('Erreur scan:', err);
                }
              }
            }, 250);
            
          }).catch(err => {
            console.error('❌ Erreur play vidéo:', err);
            setError(`Erreur lecture vidéo: ${err.message}`);
          });
        }
      }, 150);
      
    } catch (err) {
      console.error('❌ Erreur caméra:', err);
      setError(`Erreur accès caméra: ${err.message}`);
      setScanning(false);
    }
  };

  const stopScanning = () => {
    console.log('🛑 Arrêt du scanner');
    
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log('🛑 Track vidéo arrêté');
      });
      streamRef.current = null;
    }
    
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    
    setScanning(false);
  };

  useEffect(() => {
    return () => {
      stopScanning();
    };
  }, []);

  const verifyReservation = async (reservationId) => {
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/scan/${reservationId.trim()}`);

      if (!response.ok) {
        if (response.status === 404) {
          setError('❌ Réservation introuvable');
        } else {
          setError('❌ Erreur lors de la vérification');
        }
        return;
      }

      const data = await response.json();
      setResult(data);
      console.log('✅ Réservation vérifiée:', data);
    } catch (err) {
      setError('❌ Erreur réseau');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = () => {
    if (manualId.trim()) {
      verifyReservation(manualId.trim());
    }
  };

  const getStatusDisplay = (status) => {
    switch (status) {
      case 'paid':
        return {
          icon: <CheckCircle className="w-12 h-12" />,
          text: 'PAYÉ ✓',
          bgColor: 'bg-green-500',
          textColor: 'text-white'
        };
      case 'pending':
        return {
          icon: <Clock className="w-12 h-12" />,
          text: 'EN ATTENTE',
          bgColor: 'bg-yellow-500',
          textColor: 'text-white'
        };
      case 'canceled':
        return {
          icon: <XCircle className="w-12 h-12" />,
          text: 'ANNULÉ',
          bgColor: 'bg-red-500',
          textColor: 'text-white'
        };
      default:
        return {
          icon: <XCircle className="w-12 h-12" />,
          text: status,
          bgColor: 'bg-gray-500',
          textColor: 'text-white'
        };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const resetScan = () => {
    setResult(null);
    setError('');
    setManualId('');
  };

  return (
    <div className="min-h-screen bg-gray-50" style={{ paddingTop: '20px', paddingBottom: '20px' }}>
      {/* Header */}
      <div className="px-4 mb-6">
        <div className="text-center mb-4">
          <img 
            src="/logo.png" 
            alt="Logo" 
            className="mx-auto mb-3"
            style={{ height: '60px', width: 'auto' }}
          />
          <h1 
            className="text-3xl font-bold mb-2" 
            style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}
          >
            Scanner de Billets
          </h1>
          <p className="text-gray-600">Lignages de Bruxelles</p>
        </div>
      </div>

      <div className="px-4 max-w-2xl mx-auto">
        {/* Résultat affiché */}
        {result && (
          <div className="mb-6 space-y-4">
            {/* Status Badge - Grand et visible */}
            <div 
              className={`${getStatusDisplay(result.payment_status).bgColor} ${getStatusDisplay(result.payment_status).textColor} rounded-2xl p-6 text-center shadow-lg`}
            >
              <div className="flex flex-col items-center gap-3">
                {getStatusDisplay(result.payment_status).icon}
                <div className="text-3xl font-bold">
                  {getStatusDisplay(result.payment_status).text}
                </div>
              </div>
            </div>

            {/* Informations événement */}
            <div className="bg-white rounded-xl p-5 shadow-md">
              <div className="flex items-start gap-3 mb-4">
                <Calendar className="w-6 h-6 flex-shrink-0 mt-1" style={{ color: 'rgb(196, 150, 96)' }} />
                <div>
                  <div className="font-semibold text-gray-500 text-sm mb-1">ÉVÉNEMENT</div>
                  <div className="text-xl font-bold" style={{ color: 'rgb(196, 150, 96)' }}>
                    {result.event_title || 'N/A'}
                  </div>
                </div>
              </div>
              
              {result.event_start && (
                <div className="flex items-center gap-3 text-gray-700 mb-2">
                  <Clock className="w-5 h-5" />
                  <span>{formatDate(result.event_start)}</span>
                </div>
              )}
              
              {result.event_location && (
                <div className="flex items-center gap-3 text-gray-700">
                  <MapPin className="w-5 h-5" />
                  <span>{result.event_location}</span>
                </div>
              )}
            </div>

            {/* Informations acheteur */}
            <div className="bg-white rounded-xl p-5 shadow-md">
              <div className="flex items-start gap-3 mb-3">
                <User className="w-6 h-6 flex-shrink-0 mt-1" style={{ color: 'rgb(196, 150, 96)' }} />
                <div>
                  <div className="font-semibold text-gray-500 text-sm mb-1">ACHETEUR</div>
                  <div className="text-lg font-bold text-gray-800">
                    {result.buyer_name}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-3 text-gray-700">
                <Mail className="w-5 h-5" />
                <span className="text-sm break-all">{result.buyer_email}</span>
              </div>
            </div>

            {/* Montant */}
            {result.amount_total && (
              <div className="bg-white rounded-xl p-5 shadow-md">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Euro className="w-6 h-6" style={{ color: 'rgb(196, 150, 96)' }} />
                    <span className="font-semibold text-gray-500">MONTANT TOTAL</span>
                  </div>
                  <div className="text-2xl font-bold" style={{ color: 'rgb(196, 150, 96)' }}>
                    {result.amount_total} €
                  </div>
                </div>
              </div>
            )}

            {/* Détails tarifs */}
            {result.tariffs_details && result.tariffs_details.length > 0 && (
              <div className="bg-white rounded-xl p-5 shadow-md">
                <div className="font-semibold text-gray-500 text-sm mb-3">DÉTAILS DES BILLETS</div>
                {result.tariffs_details.map((tariff, idx) => (
                  <div key={idx} className="flex justify-between items-center py-2 border-b last:border-b-0">
                    <div>
                      <div className="font-medium">{tariff.label}</div>
                      <div className="text-sm text-gray-500">Quantité: {tariff.quantity}</div>
                    </div>
                    <div className="font-bold">{tariff.price} €</div>
                  </div>
                ))}
              </div>
            )}

            {/* Méthode de paiement */}
            {result.payment_method && (
              <div className="bg-white rounded-xl p-4 shadow-md text-center text-sm text-gray-600">
                <span className="font-medium">Méthode: </span>
                {result.payment_method === 'stripe' ? 'Carte bancaire (Stripe)' : 
                 result.payment_method === 'virement' ? 'Virement bancaire' : 
                 result.payment_method === 'sur_place' ? 'Paiement sur place' : 
                 result.payment_method}
              </div>
            )}

            {/* Bouton Nouveau scan */}
            <button
              onClick={resetScan}
              className="w-full py-4 text-white rounded-xl text-lg font-semibold shadow-lg"
              style={{ backgroundColor: 'rgb(196, 150, 96)' }}
            >
              🔄 Nouveau Scan
            </button>
          </div>
        )}

        {/* Scanner et saisie manuelle */}
        {!result && (
          <>
            {/* Message d'erreur */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border-2 border-red-200 rounded-xl text-red-800 text-center font-medium">
                {error}
              </div>
            )}

            {/* Scanner */}
            {!scanning ? (
              <div className="mb-6">
                <button
                  onClick={startScanning}
                  disabled={cameras.length === 0 || loading}
                  className="w-full py-6 text-white rounded-xl flex items-center justify-center gap-3 text-xl font-semibold shadow-lg disabled:opacity-50"
                  style={{ backgroundColor: 'rgb(196, 150, 96)' }}
                >
                  <Camera className="w-8 h-8" />
                  {loading ? 'Vérification...' : 
                   cameras.length === 0 ? 'Aucune caméra détectée' : 
                   '📷 Scanner QR Code'}
                </button>
              </div>
            ) : (
              <div className="mb-6">
                <div className="relative bg-black rounded-xl overflow-hidden shadow-xl">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full"
                    style={{
                      minHeight: '400px',
                      maxHeight: '500px',
                      objectFit: 'cover',
                    }}
                  />
                  
                  <canvas ref={canvasRef} style={{ display: 'none' }} />
                  
                  {/* Cadre de scan */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="relative" style={{ width: '280px', height: '280px' }}>
                      <div className="absolute top-0 left-0 w-12 h-12 border-t-4 border-l-4 border-green-400"></div>
                      <div className="absolute top-0 right-0 w-12 h-12 border-t-4 border-r-4 border-green-400"></div>
                      <div className="absolute bottom-0 left-0 w-12 h-12 border-b-4 border-l-4 border-green-400"></div>
                      <div className="absolute bottom-0 right-0 w-12 h-12 border-b-4 border-r-4 border-green-400"></div>
                    </div>
                  </div>
                  
                  <button
                    onClick={stopScanning}
                    className="absolute top-4 right-4 bg-red-500 text-white p-3 rounded-full hover:bg-red-600 transition z-10 shadow-lg"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                <p className="text-center mt-4 text-gray-700 font-medium text-lg">
                  📱 Positionnez le QR code dans le cadre
                </p>
              </div>
            )}

            {/* Saisie manuelle */}
            {!scanning && (
              <div className="bg-white rounded-xl p-5 shadow-md">
                <p className="text-gray-700 mb-4 font-medium text-center">
                  Ou entrez le numéro de réservation :
                </p>
                <div className="flex flex-col gap-3">
                  <input
                    type="text"
                    value={manualId}
                    onChange={(e) => setManualId(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleManualSubmit()}
                    placeholder="UUID de réservation"
                    disabled={loading}
                    className="w-full px-4 py-4 border-2 rounded-xl focus:ring-2 focus:ring-amber-500 text-lg disabled:opacity-50"
                  />
                  <button
                    onClick={handleManualSubmit}
                    disabled={!manualId.trim() || loading}
                    className="w-full py-4 text-white rounded-xl flex items-center justify-center gap-2 text-lg font-semibold shadow-md disabled:opacity-50"
                    style={{ backgroundColor: 'rgb(34, 197, 94)' }}
                  >
                    <CheckCircle className="w-6 h-6" />
                    {loading ? 'Vérification...' : 'Vérifier'}
                  </button>
                </div>
              </div>
            )}

            {/* Sélecteur de caméra */}
            {!scanning && cameras.length > 1 && (
              <div className="mt-4 bg-white rounded-xl p-4 shadow-md">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Choisir la caméra :
                </label>
                <select
                  value={selectedCamera}
                  onChange={(e) => setSelectedCamera(e.target.value)}
                  className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-amber-500"
                >
                  {cameras.map((camera, index) => (
                    <option key={camera.deviceId} value={camera.deviceId}>
                      {camera.label || `Caméra ${index + 1}`}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ScanPage;
